import React, { memo, useRef, useCallback } from 'react';
import { Button } from '@common/components/button';
import TextAreaInput from '@common/components/form/inputs/textarea/textarea-input';
import { useTranslation } from 'react-i18next';

interface CompetencyCommentSectionProps {
  competencyId: string;
  comment: string;
  showComment: boolean;
  isEditing: boolean;
  editingValue: string;
  canEdit: boolean;
  onEdit: (competencyId: string, comment: string) => void;
  onSave: (competencyId: string) => void;
  onCancel: () => void;
  onEditingValueChange: (value: string) => void;
}

const CompetencyCommentSection = memo<CompetencyCommentSectionProps>(({
  competencyId,
  comment,
  showComment,
  isEditing,
  editingValue,
  canEdit,
  onEdit,
  onSave,
  onCancel,
  onEditingValueChange,
}) => {
  const { t } = useTranslation();
  const editingCommentInputRef = useRef<HTMLTextAreaElement>(null);

  const handleEditClick = useCallback(() => {
    onEdit(competencyId, comment);
  }, [onEdit, competencyId, comment]);

  const handleSaveClick = useCallback(() => {
    onSave(competencyId);
  }, [onSave, competencyId]);

  const handleContainerClick = useCallback(() => {
    if (canEdit) {
      handleEditClick();
    }
  }, [canEdit, handleEditClick]);

  // Auto-focus and set cursor position when editing
  React.useEffect(() => {
    if (isEditing && editingCommentInputRef.current) {
      const timer = setTimeout(() => {
        if (!editingCommentInputRef.current) return;
        editingCommentInputRef.current.focus();
        const { length } = editingCommentInputRef.current.value;
        editingCommentInputRef.current.setSelectionRange(length || 0, length || 0);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isEditing]);

  if (isEditing) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-3">
        <div className="tw-bg-gray-custom-50 tw-border-[2px] !tw-rounded-lg">
          <TextAreaInput
            ref={editingCommentInputRef}
            value={editingValue}
            onChange={onEditingValueChange}
            placeholder={t('performance:write_your_comment')}
            minRows={3}
            className="!tw-bg-white !tw-border-gray-border"
          />
        </div>
        <div className="tw-flex tw-flex-row tw-gap-2 tw-justify-end">
          <Button
            type="default"
            size="small"
            onClick={onCancel}
          >
            {t('performance:cancel')}
          </Button>
          <Button
            type="primary"
            size="small"
            onClick={handleSaveClick}
          >
            {t('performance:save')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="tw-flex tw-flex-row tw-items-center tw-justify-between tw-p-4
        tw-bg-gray-custom-50 tw-border-[2px] tw-border-dashed tw-border-gray-disabled
        tw-borderImage !tw-cursor-text !tw-rounded-lg"
      {...(canEdit && { onClick: handleContainerClick })}
    >
      <div
        className={`${
          showComment && comment
            ? 'tw-text-paragraph'
            : 'tw-text-gray-disabled'
        } tw-text-base tw-font-medium`}
      >
        {showComment
          ? comment || t('performance:no_comment')
          : t('performance:not_submitted')}
      </div>
      {canEdit && (
        <Button
          type="transparent"
          icon="edit"
          size="small"
          className="!tw-p-0"
          onClick={handleEditClick}
        >
          {t('performance:edit')}
        </Button>
      )}
    </div>
  );
});

CompetencyCommentSection.displayName = 'CompetencyCommentSection';

export default CompetencyCommentSection;
